/**
 * MobileStaffScheduleAgenda — read-only mobile / tablet view of the
 * Staff Schedule ( <lg breakpoint ). Renders a per-day agenda plus the
 * existing weekly management KPIs.
 *
 * Non-negotiables (Batch 4.1):
 *   • DESKTOP (≥1024px) IS NOT TOUCHED — this component only renders when
 *     the parent wraps it in `lg:hidden`.
 *   • No new mutation workflows on mobile. Secondary manager actions
 *     re-use the parent's existing handlers unchanged.
 *   • All KPI values are read from the parent (`kpis` prop) — no mobile-
 *     specific recalculation.
 *   • Payroll / attendance / rate-snapshot logic is not touched.
 */
import React, { useMemo } from "react";
import {
  ChevronLeft, ChevronRight, CalendarClock, Building2, Users, UserCheck,
  UserX, Umbrella, Heart, TrendingUp, AlertTriangle, Sunrise, Sun, Sunset,
  Moon, MoreVertical, Send, Copy, FileSpreadsheet, Printer, Sparkles,
  ShieldAlert, Clock,
} from "lucide-react";
import PersonAvatar from "@/components/PersonAvatar";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Mirror the desktop time-block classifier from StaffSchedule.jsx so the
// grouping is presentation-only and consistent between views.
function shiftType(v) {
  const h = parseInt((v.scheduled_start || "").slice(11, 13), 10);
  if (isNaN(h)) return "unspecified";
  if (h >= 6 && h < 12) return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  if (h >= 17 && h < 22) return "evening";
  return "night";
}
function timeHM(iso) {
  if (!iso) return "";
  return iso.slice(11, 16);
}

const SHIFT_META = {
  morning:    { label: "Morning",   icon: Sunrise, tone: "text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800" },
  afternoon:  { label: "Afternoon", icon: Sun,     tone: "text-sky-700 bg-sky-50 border-sky-200 dark:bg-sky-950/30 dark:text-sky-300 dark:border-sky-800" },
  evening:    { label: "Evening",   icon: Sunset,  tone: "text-violet-700 bg-violet-50 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-800" },
  night:      { label: "Night",     icon: Moon,    tone: "text-slate-100 bg-slate-800 border-slate-900" },
  unspecified:{ label: "Other",     icon: Clock,   tone: "text-slate-700 bg-slate-50 border-slate-200 dark:bg-slate-800/60 dark:text-slate-200 dark:border-slate-700" },
};
const BLOCK_ORDER = ["morning", "afternoon", "evening", "night", "unspecified"];

function formatDayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" });
}
function formatFullDay(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
}

// ---------------------------------------------------------------------------
// Shift card (read-only)
// ---------------------------------------------------------------------------
function MobileShiftCard({ visit, staff, onView }) {
  const unassigned = !visit.staff_user_id;
  const carer = staff.find((s) => s.id === visit.staff_user_id);
  const draft = !visit.published_at;
  const meta = SHIFT_META[shiftType(visit)];

  if (unassigned) {
    return (
      <button
        type="button"
        onClick={() => onView(visit)}
        data-testid={`mobile-shift-vacant-${visit.id}`}
        className="w-full text-left rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 min-h-[44px] active:bg-amber-100 dark:active:bg-amber-950/50 transition-colors"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-700 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-amber-800 dark:text-amber-300 font-bold inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Vacant — staff required
            </div>
            <div className="font-semibold text-slate-900 dark:text-slate-100 truncate mt-0.5">
              {visit.client_name || visit.client_id || "Service user"}
            </div>
            <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
              <span className="tabular-nums font-medium">
                {timeHM(visit.scheduled_start)}{visit.scheduled_end ? `–${timeHM(visit.scheduled_end)}` : ""}
              </span>
              {visit.site_name && (
                <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{visit.site_name}</span>
              )}
              {draft && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 border border-current rounded opacity-80">Draft</span>}
            </div>
          </div>
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onView(visit)}
      data-testid={`mobile-shift-${visit.id}`}
      className="w-full text-left rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 min-h-[44px] active:bg-slate-50 dark:active:bg-slate-800 transition-colors"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 shrink-0">
          <PersonAvatar person={carer || { name: "Staff" }} size="sm" testid={`mobile-shift-avatar-${visit.id}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 dark:text-slate-100 truncate">
              {carer?.name || carer?.email || "Assigned"}
            </span>
            <span className={`text-[9px] uppercase font-semibold tracking-widest px-1.5 py-0.5 rounded ${meta.tone}`}>
              {meta.label}
            </span>
            {draft && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 border border-slate-400 dark:border-slate-500 rounded text-slate-600 dark:text-slate-300">Draft</span>}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">
            {carer?.role ? carer.role.replace(/_/g, " ") : "Care worker"}
          </div>
          <div className="text-xs text-slate-700 dark:text-slate-300 mt-1 tabular-nums font-medium">
            {timeHM(visit.scheduled_start)}{visit.scheduled_end ? `–${timeHM(visit.scheduled_end)}` : ""}
            {visit.client_name && (
              <span className="ml-2 text-slate-600 dark:text-slate-400 font-normal">
                · {visit.client_name}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
            {visit.site_name && (
              <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{visit.site_name}</span>
            )}
            {visit.status === "completed" && (
              <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 font-medium">
                <UserCheck className="w-3 h-3" /> Completed
              </span>
            )}
            {visit.status === "missed" && (
              <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-400 font-medium">
                <AlertTriangle className="w-3 h-3" /> Missed
              </span>
            )}
            {visit.check_in_at && !visit.check_out_at && (
              <span className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-400 font-medium">
                <Clock className="w-3 h-3" /> Checked in
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// KPI chip for the swipeable strip
// ---------------------------------------------------------------------------
function KpiChip({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    ok: "border-emerald-200 bg-white text-emerald-800 dark:bg-slate-900 dark:border-emerald-800 dark:text-emerald-300",
    warn: "border-amber-200 bg-white text-amber-800 dark:bg-slate-900 dark:border-amber-800 dark:text-amber-300",
    danger: "border-red-200 bg-white text-red-800 dark:bg-slate-900 dark:border-red-800 dark:text-red-300",
    info: "border-slate-200 bg-white text-slate-800 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100",
  };
  return (
    <div
      className={`snap-start shrink-0 w-[150px] rounded-lg border p-3 ${tones[tone]}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 opacity-75 shrink-0" />
        <div className="text-[9px] uppercase tracking-widest font-semibold opacity-80 truncate">{label}</div>
      </div>
      <div className="text-2xl font-heading font-semibold tabular-nums mt-1 leading-tight">
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function MobileStaffScheduleAgenda({
  selectedDay,
  today,
  weekStart,
  days,
  heatmapWeeks,
  heatmap,
  visits,
  staff,
  timeOff,
  accessibleSites,
  filterSite,
  kpis,
  canManage,
  draftCount,
  publishing,
  isOff,
  onPrevDay,
  onNextDay,
  onGoToday,
  onPickWeek,
  onSetFilterSite,
  onViewShift,
  onOpenAi,
  onCopyWeek,
  onNotify,
  onExport,
  onPrint,
  onOpenTimeOff,
  onPublish,
}) {
  // Visits on the selected day, filtered by site (never mutates parent).
  const dayVisits = useMemo(() => {
    return visits
      .filter((v) => (v.scheduled_start || "").startsWith(selectedDay))
      .filter((v) => filterSite === "all" ? true : v.site_id === filterSite)
      .sort((a, b) => (a.scheduled_start || "").localeCompare(b.scheduled_start || ""));
  }, [visits, selectedDay, filterSite]);

  // Group visits by shift block (morning/afternoon/evening/night/unspecified).
  const grouped = useMemo(() => {
    const map = { morning: [], afternoon: [], evening: [], night: [], unspecified: [] };
    dayVisits.forEach((v) => { map[shiftType(v)].push(v); });
    return map;
  }, [dayVisits]);

  // Operational summary numbers derived from EXISTING day visits — no new
  // API calls, no new formulas. `onDutyToday` reuses parent's kpis when
  // the selected day is today, otherwise reflects staff on shift that day.
  const daySummary = useMemo(() => {
    const uniqueStaff = new Set(dayVisits.map((v) => v.staff_user_id).filter(Boolean));
    const vacant = dayVisits.filter((v) => !v.staff_user_id).length;
    const onDuty = selectedDay === today ? kpis.onDutyToday : uniqueStaff.size;
    return { staff: uniqueStaff.size, onDuty, vacant };
  }, [dayVisits, selectedDay, today, kpis.onDutyToday]);

  // Staff on leave today (informational only — read from existing timeOff).
  const onLeaveToday = useMemo(() => {
    return timeOff.filter((t) => t.start_date <= selectedDay && t.end_date >= selectedDay);
  }, [timeOff, selectedDay]);

  const isToday = selectedDay === today;

  return (
    <div
      className="lg:hidden flex-1 min-h-0 overflow-auto bg-slate-50 dark:bg-slate-950 print:hidden"
      data-testid="mobile-staff-schedule"
    >
      <div className="px-3 pt-3 pb-6 space-y-3 max-w-full">
        {/* Section title */}
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Care Delivery</div>
            <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100" data-testid="mobile-schedule-title">
              Staff Schedule
            </h2>
          </div>
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-md text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 active:bg-slate-100 dark:active:bg-slate-800"
                  aria-label="More manager actions"
                  data-testid="mobile-schedule-more"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="mobile-schedule-more-menu">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">Manager actions</DropdownMenuLabel>
                <DropdownMenuItem onSelect={onPublish} disabled={publishing || draftCount === 0} data-testid="mobile-more-publish">
                  <Send className="w-4 h-4 mr-2 text-emerald-600" />
                  {draftCount > 0 ? `Publish ${draftCount} change${draftCount === 1 ? "" : "s"}` : "All published"}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onOpenAi} data-testid="mobile-more-ai">
                  <Sparkles className="w-4 h-4 mr-2 text-violet-600" /> AI Optimise
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={onCopyWeek} data-testid="mobile-more-copy">
                  <Copy className="w-4 h-4 mr-2" /> Copy Week
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onNotify} data-testid="mobile-more-notify">
                  <Send className="w-4 h-4 mr-2" /> Notify
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onExport} data-testid="mobile-more-export">
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Export CSV
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onPrint} data-testid="mobile-more-print">
                  <Printer className="w-4 h-4 mr-2" /> Print
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={onOpenTimeOff} data-testid="mobile-more-timeoff">
                  <Umbrella className="w-4 h-4 mr-2" /> Log leave
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Day navigator */}
        <div className="flex items-center gap-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-1">
          <button
            type="button"
            onClick={onPrevDay}
            data-testid="mobile-schedule-prev-day"
            aria-label="Previous day"
            className="w-11 h-11 inline-flex items-center justify-center rounded-md text-slate-600 dark:text-slate-300 active:bg-slate-100 dark:active:bg-slate-800"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={onGoToday}
            data-testid="mobile-schedule-today"
            className={`flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 h-11 rounded-md text-sm font-semibold ${
              isToday
                ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300"
                : "text-slate-800 dark:text-slate-100 active:bg-slate-100 dark:active:bg-slate-800"
            }`}
          >
            <CalendarClock className="w-4 h-4 shrink-0" />
            <span className="truncate">
              {isToday ? "Today · " : ""}{formatDayLabel(selectedDay)}
            </span>
          </button>
          <button
            type="button"
            onClick={onNextDay}
            data-testid="mobile-schedule-next-day"
            aria-label="Next day"
            className="w-11 h-11 inline-flex items-center justify-center rounded-md text-slate-600 dark:text-slate-300 active:bg-slate-100 dark:active:bg-slate-800"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Site + Week selectors */}
        <div className="grid grid-cols-2 gap-2">
          <Select value={filterSite} onValueChange={onSetFilterSite}>
            <SelectTrigger className="h-11 bg-white dark:bg-slate-900" data-testid="mobile-schedule-site">
              <Building2 className="w-4 h-4 mr-1.5 text-slate-500 shrink-0" />
              <SelectValue placeholder="All sites" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sites / houses</SelectItem>
              {accessibleSites.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={weekStart} onValueChange={onPickWeek}>
            <SelectTrigger className="h-11 bg-white dark:bg-slate-900" data-testid="mobile-schedule-week">
              <CalendarClock className="w-4 h-4 mr-1.5 text-slate-500 shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {heatmapWeeks.map((wk) => {
                const count = heatmap[wk] || 0;
                const wkDate = new Date(wk + "T00:00:00");
                return (
                  <SelectItem key={wk} value={wk} data-testid={`mobile-schedule-week-opt-${wk}`}>
                    Week of {wkDate.toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
                    <span className="ml-2 text-[10px] text-slate-500 tabular-nums">
                      · {count} shift{count === 1 ? "" : "s"}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Operational summary */}
        <div
          className="grid grid-cols-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden"
          data-testid="mobile-schedule-operational"
        >
          <div className="p-3 text-center">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Staff</div>
            <div className="text-lg font-heading font-semibold tabular-nums text-slate-900 dark:text-slate-100 mt-0.5" data-testid="mobile-op-staff">
              {daySummary.staff}
            </div>
          </div>
          <div className="p-3 text-center border-x border-slate-100 dark:border-slate-800">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">On duty</div>
            <div className="text-lg font-heading font-semibold tabular-nums text-emerald-700 dark:text-emerald-400 mt-0.5" data-testid="mobile-op-onduty">
              {daySummary.onDuty}
            </div>
          </div>
          <div className="p-3 text-center">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Vacant</div>
            <div className={`text-lg font-heading font-semibold tabular-nums mt-0.5 ${daySummary.vacant > 0 ? "text-amber-700 dark:text-amber-400" : "text-slate-500"}`} data-testid="mobile-op-vacant">
              {daySummary.vacant}
            </div>
          </div>
        </div>

        {/* Weekly management KPIs — snap-scroll */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5 px-1">
            Week of {new Date(weekStart + "T00:00:00").toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
            <span className="ml-2 text-slate-400 normal-case tracking-normal">swipe →</span>
          </div>
          <div
            className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1 -mx-3 px-3 [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none" }}
            data-testid="mobile-schedule-kpi-strip"
          >
            <KpiChip icon={Users} label="Staff Scheduled" value={kpis.scheduledStaff} tone="info" testid="mobile-kpi-scheduled" />
            <KpiChip icon={UserCheck} label="On Duty Today" value={kpis.onDutyToday} tone="ok" testid="mobile-kpi-on-duty" />
            <KpiChip icon={UserX} label="Vacant Shifts" value={kpis.vacantShifts} tone={kpis.vacantShifts > 0 ? "warn" : "ok"} testid="mobile-kpi-vacant" />
            <KpiChip icon={Umbrella} label="Annual Leave" value={kpis.annualLeave} tone="info" testid="mobile-kpi-holiday" />
            <KpiChip icon={Heart} label="Sick Leave" value={kpis.sickLeave} tone={kpis.sickLeave > 0 ? "warn" : "ok"} testid="mobile-kpi-sick" />
            <KpiChip icon={TrendingUp} label="Overtime Hours" value={`${kpis.overtimeHours}h`} tone={kpis.overtimeHours > 0 ? "danger" : "ok"} testid="mobile-kpi-overtime" />
          </div>
        </div>

        {/* On leave today informational strip */}
        {onLeaveToday.length > 0 && (
          <div
            className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3"
            data-testid="mobile-schedule-leave-strip"
          >
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5 inline-flex items-center gap-1">
              <Umbrella className="w-3.5 h-3.5" /> On leave today · {onLeaveToday.length}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {onLeaveToday.map((t) => {
                const s = staff.find((x) => x.id === t.staff_user_id);
                if (!s) return null;
                return (
                  <span
                    key={t.id || `${t.staff_user_id}-${t.start_date}`}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                    data-testid={`mobile-schedule-leave-${t.staff_user_id}`}
                  >
                    <span className="font-medium truncate max-w-[120px]">{s.name || s.email}</span>
                    <span className="text-[9px] uppercase tracking-widest opacity-70">· {t.kind}</span>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Shift groups */}
        {dayVisits.length === 0 ? (
          <div
            className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 text-center"
            data-testid="mobile-schedule-empty"
          >
            <CalendarClock className="w-8 h-8 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">No shifts on this day</div>
            <div className="text-xs text-slate-500 mt-1">
              {formatFullDay(selectedDay)}
            </div>
            <div className="text-[11px] text-slate-400 mt-2">
              Use the week selector above to jump to a week with shifts.
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="mobile-schedule-shifts">
            {BLOCK_ORDER.map((block) => {
              const list = grouped[block];
              if (!list || list.length === 0) return null;
              const meta = SHIFT_META[block];
              const Icon = meta.icon;
              return (
                <div key={block} data-testid={`mobile-schedule-block-${block}`}>
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${meta.tone}`}>
                      <Icon className="w-3.5 h-3.5" /> {meta.label}
                    </div>
                    <div className="text-[10px] text-slate-500 tabular-nums">
                      {list.length} shift{list.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="space-y-2">
                    {list.map((v) => (
                      <MobileShiftCard
                        key={v.id}
                        visit={v}
                        staff={staff}
                        onView={onViewShift}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
